import type { LotteryId } from '#api/shared/@types/generated/backend/graphql-schema-types'

import { EditorRestorePlaysModal } from '#components'
import { QUERY_REVALIDATE } from '~/constants/queryParams'

import type { Config, PlayLike, ReactiveSource } from '../@types/editor'
import type { useEditorOptionsStore } from '../stores/options'

import { EDITOR_QUERY_QUICK_PICK } from '../constants/editor'
import { toQuickPickQuantity } from './editorQuickPickQuery'

export type RestorablePlayStore = {
  clearSavedPlays: () => void
  configVersion: string
  // method syntax: the stores narrow the play type, which a property signature would reject
  isPlayEmpty(play: PlayLike): boolean
  plays: PlayLike[]
  restoreSavedPlays: (config: Config) => void
  savedPlays: PlayLike[]
  stashPlays: () => void
}

export type RestorableOptions = {
  reapplyFromQuery: () => void
  store: Pick<ReturnType<typeof useEditorOptionsStore>, 'clearSaved' | 'restoreSaved' | 'stash'>
}

// above the cutoff modal (0), below the global notices (legacy warning 90, acknowledgement 100)
export const EDITOR_RESTORE_PLAYS_PRIORITY = 10

/*
 * Plays persisted by an earlier session are not shown right away. The page starts blank and a modal
 * asks the customer whether to restore them. Within one SPA session the question is asked at most once
 * per lottery: the answer lives in memory, so a full reload asks again.
 */
export const useEditorRestorePlays = (
  lotteryId: LotteryId,
  store: RestorablePlayStore,
  options: RestorableOptions,
  configRef: ReactiveSource<Maybe<Config>>,
  pageReady: ReactiveSource<boolean>,
) => {
  const route = useRoute()
  const { registerModal } = useModal()
  const { log } = useDatadog()
  const { withoutAnimation } = useEditorPlayListAnimation()

  const answered = useState<Partial<Record<LotteryId, boolean>>>(
    'editor-restore-plays-answered',
    () => ({}),
  )

  const hasPlays = () => store.plays.some((play) => !store.isPlayEmpty(play))

  const markAnswered = () => {
    answered.value[lotteryId] = true
  }

  const dropSaved = () => {
    store.clearSavedPlays()
    options.store.clearSaved()
  }

  // runs on the client once the stores have read the storage and before they are set up
  const prepare = (config: Config) => {
    // a valid quick pick link replaces the stored plays, a checkout revalidation needs them as they are
    if (toQuickPickQuantity(route.query[EDITOR_QUERY_QUICK_PICK], config)) markAnswered()
    if (route.query[QUERY_REVALIDATE]) markAnswered()

    // an empty version counts as current, like in resetCacheOnStaleVersion
    const isStale = !!store.configVersion && store.configVersion !== config.version
    if (answered.value[lotteryId] || isStale) return dropSaved()

    if (hasPlays()) {
      store.stashPlays()
      options.store.stash()
    }
  }

  const decide = (restore: boolean) => {
    const config = toValue(configRef)
    const plays = store.savedPlays.length

    if (restore && config) {
      withoutAnimation(() => store.restoreSavedPlays(config))
      options.store.restoreSaved(config)
      options.reapplyFromQuery()
    } else {
      dropSaved()
    }

    markAnswered()

    log('info', {
      component: 'editorRestorePlays',
      message: restore ? 'Customer restored saved plays' : 'Customer dropped saved plays',
      messageContext: { lotteryId, plays },
    })
  }

  registerModal({
    component: EditorRestorePlaysModal,
    onCancel: () => decide(false),
    onClose: () => decide(false),
    onConfirm: () => decide(true),
    priority: EDITOR_RESTORE_PLAYS_PRIORITY,
    // the stores are prepared before the page has replaced the previous one on screen
    when: () => toValue(pageReady) && store.savedPlays.length > 0,
  })

  return { hasPlays, markAnswered, prepare }
}
